import Axios from "axios";
import { toPersistedToolActivities } from "../message/mcp-chat-tool-activity-state";
import { MCP_CHAT_API_URL } from "../shared/mcp-chat-config";
import { getStoredEffort } from "../shared/mcp-chat-effort";
import type {
  ChangesSummary,
  ChatMode,
  EffortTier,
  CompletedToolActivity,
  DdlOp,
  InterruptData,
  McpChatMessage,
  MentionReference,
  QuestionnaireProgress,
  SelectedElementAttachment,
  SelectedElementSegment
} from "../shared/mcp-chat-types";
import { planJsonForTab } from "../shared/plan-ownership";
import { CHAT_SAVE_FAILED_EVENT, CHAT_SAVE_RECOVERED_EVENT } from "./mcp-chat-events";

// ── Types ──

interface PersistedMessage {
  id: string;
  role: "user" | "assistant" | "tool_status";
  content: string;
  timestamp: number;
  isError?: boolean;
  toolName?: string;
  toolStatus?: "running" | "completed" | "error";
  hadImages?: boolean;
  hadFiles?: boolean;
  stagedOpIdsBeforeSend?: string[];
  ddlOps?: DdlOp[];
  changesSummary?: ChangesSummary;
  completedToolActivities?: CompletedToolActivity[];
  planStatus?: "pending" | "approved" | "rejected";
  /**
   * KOA-18136 — the spec card's decision, persisted for the same reason `planStatus` is.
   *
   * The approve/reject buttons render on any assistant message that LOOKS like a spec and
   * carries no decision yet, so a decision that lives only in memory comes back as a live
   * "Build" button after any reload. On live `edf97c79` that button was pressed a second
   * time and the identical approved spec (same version stamp) re-entered the pipeline as a
   * from-scratch full-stack build over a finished app — it renamed six process steps,
   * attached the wrong action to two of them, and turned three clean design errors out of a
   * project that had zero.
   */
  specStatus?: "approved" | "rejected";
  interruptData?: InterruptData;
  selectedElementAttachments?: SelectedElementAttachment[];
  selectedElementSegments?: SelectedElementSegment[];
  /** @deprecated Legacy singular field. Loader migrates to `selectedElementAttachments`. */
  selectedElementAttachment?: SelectedElementAttachment;
  responseDurationMs?: number;
  mentions?: MentionReference[];
  chatRequestId?: string;
  userRating?: number;
  explorationContent?: string;
  suggestions?: string[];
}

export interface PersistedChatTab {
  id: string;
  name: string;
  sessionId: string | null;
  messages: PersistedMessage[];
  isCustomName?: boolean;
  chatMode?: string;
  planMode?: boolean;
  planJson?: string | null;
  /** Ms-epoch set on archived tabs; unset while the tab is live. */
  archivedAt?: number;
}

export type { PersistedMessage };

export interface PersistedChatState {
  version: 1;
  savedAt: number;
  activeTabId: string;
  tabs: PersistedChatTab[];
  /** IDs of tabs the client wants archived atomically with this save.
   *  Used by `handleCloseTab` / `handleClearChat` to pair the tab removal
   *  with archive-retention metadata without needing a separate POST. */
  archivedTabIds?: string[];
}

// ── Constants ──

const LEGACY_STORAGE_KEY = "kuika_mcp_chat";
const MAX_PERSISTED_MESSAGES = 50;
const FETCH_TIMEOUT_MS = 5000;
const SAVE_MAX_RETRIES = 3;
const SAVE_BACKOFF_BASE_MS = 500;

// ── Helpers ──

function apiUrl(projectId: string): string {
  return `${MCP_CHAT_API_URL}/chat-history/${encodeURIComponent(projectId)}`;
}

interface PersistenceResponse {
  status: number;
  ok: boolean;
  json(): Promise<unknown>;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null;
}

function isPersistedChatState(value: unknown): value is PersistedChatState {
  if (!isObject(value)) return false;
  if (value.version !== 1) return false;
  if (typeof value.savedAt !== "number") return false;
  if (typeof value.activeTabId !== "string") return false;
  if (!Array.isArray(value.tabs)) return false;
  return true;
}

interface SavedAtEnvelope {
  savedAt: number;
}

function isSavedAtEnvelope(value: unknown): value is SavedAtEnvelope {
  return isObject(value) && typeof value.savedAt === "number";
}

/**
 * Axios-backed replacement for `fetch` with timeout. Routes every request
 * through the global Axios instance so the auth interceptor (Authorization
 * + domainName headers) applies — `fetch` would have bypassed it. Mirrors
 * the parts of the `Response` interface this module's call sites use
 * (`status`, `ok`, `json()`) so the migration is local to this helper.
 */
async function fetchWithTimeout(
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string }
): Promise<PersistenceResponse> {
  const res = await Axios.request({
    url,
    method: init?.method ?? "GET",
    data: init?.body,
    headers: init?.headers,
    timeout: FETCH_TIMEOUT_MS,
    validateStatus: () => true
  });
  return {
    status: res.status,
    ok: res.status >= 200 && res.status < 300,
    json: async () => res.data
  };
}

export function buildProjectState(
  tabs: Array<{
    id: string;
    name: string;
    sessionId: string | null;
    isCustomName?: boolean;
    messages: McpChatMessage[];
    chatMode?: string;
    planMode?: boolean;
    planJson?: string | null;
  }>,
  activeTabId: string,
  archivedTabIds?: string[]
): PersistedChatState {
  const persistedTabs: PersistedChatTab[] = tabs.map((tab) => {
    const msgs = tab.messages.slice(-MAX_PERSISTED_MESSAGES).map((msg): PersistedMessage => {
      const persisted: PersistedMessage = {
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      };
      if (msg.isError) persisted.isError = true;
      if (msg.toolName) persisted.toolName = msg.toolName;
      if (msg.toolStatus) persisted.toolStatus = msg.toolStatus;
      if (msg.images && msg.images.length > 0) persisted.hadImages = true;
      if (msg.files && msg.files.length > 0) persisted.hadFiles = true;
      if (msg.stagedOpIdsBeforeSend) persisted.stagedOpIdsBeforeSend = msg.stagedOpIdsBeforeSend;
      if (msg.ddlOps && msg.ddlOps.length > 0) persisted.ddlOps = msg.ddlOps;
      if (msg.changesSummary) persisted.changesSummary = msg.changesSummary;
      if (msg.completedToolActivities) {
        persisted.completedToolActivities = toPersistedToolActivities(msg.completedToolActivities);
      }
      if (msg.planStatus) persisted.planStatus = msg.planStatus;
      if (msg.specStatus) persisted.specStatus = msg.specStatus;
      if (msg.interruptData) persisted.interruptData = msg.interruptData;
      if (msg.selectedElementAttachments && msg.selectedElementAttachments.length > 0) {
        persisted.selectedElementAttachments = msg.selectedElementAttachments;
      }
      if (msg.selectedElementSegments && msg.selectedElementSegments.length > 0) {
        persisted.selectedElementSegments = msg.selectedElementSegments;
      }
      if (msg.responseDurationMs) persisted.responseDurationMs = msg.responseDurationMs;
      if (msg.mentions && msg.mentions.length > 0) persisted.mentions = msg.mentions;
      if (msg.chatRequestId) persisted.chatRequestId = msg.chatRequestId;
      if (msg.userRating != null) persisted.userRating = msg.userRating;
      if (msg.explorationContent) persisted.explorationContent = msg.explorationContent;
      if (msg.suggestions && msg.suggestions.length > 0) persisted.suggestions = msg.suggestions;
      return persisted;
    });

    return {
      id: tab.id,
      name: tab.name,
      sessionId: tab.sessionId,
      messages: msgs,
      isCustomName: tab.isCustomName || undefined,
      chatMode: tab.chatMode !== "designer" ? tab.chatMode : undefined,
      planMode: tab.planMode || undefined,
      planJson: tab.planJson || undefined
    };
  });

  return {
    version: 1,
    savedAt: Date.now(),
    activeTabId,
    tabs: persistedTabs,
    archivedTabIds: archivedTabIds && archivedTabIds.length > 0 ? [...archivedTabIds] : undefined
  };
}

// ── Retry helper ──

async function saveWithRetry(url: string, body: string): Promise<void> {
  for (let attempt = 0; attempt < SAVE_MAX_RETRIES; attempt++) {
    try {
      const res = await fetchWithTimeout(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body
      });
      if (res.ok) {
        if (saveHasFailed) {
          saveHasFailed = false;
          window.dispatchEvent(new CustomEvent(CHAT_SAVE_RECOVERED_EVENT));
        }
        return;
      }
      console.warn(`[mcp-chat-persistence] Save returned ${res.status}, attempt ${attempt + 1}/${SAVE_MAX_RETRIES}`);
    } catch (err: any) {
      console.warn(`[mcp-chat-persistence] Save failed (attempt ${attempt + 1}/${SAVE_MAX_RETRIES}):`, err.message);
    }
    if (attempt < SAVE_MAX_RETRIES - 1) {
      await new Promise((resolve) => setTimeout(resolve, SAVE_BACKOFF_BASE_MS * Math.pow(2, attempt)));
    }
  }
  if (!saveHasFailed) {
    saveHasFailed = true;
    window.dispatchEvent(new CustomEvent(CHAT_SAVE_FAILED_EVENT));
  }
}

// ── Exported Functions ──

export function saveChatState(
  projectId: string | null,
  tabs: Array<{
    id: string;
    name: string;
    sessionId: string | null;
    isCustomName?: boolean;
    messages: McpChatMessage[];
    chatMode?: string;
    planMode?: boolean;
    planJson?: string | null;
  }>,
  activeTabId: string,
  archivedTabIds?: string[]
): void {
  if (!projectId) return;

  const state = buildProjectState(tabs, activeTabId, archivedTabIds);

  // Track latest savedAt for sync
  lastKnownSavedAt = state.savedAt;

  // Fire-and-forget with internal retry
  saveWithRetry(apiUrl(projectId), JSON.stringify(state));
}

/**
 * Synchronous save using navigator.sendBeacon for beforeunload.
 * Beacon requests survive page unload — browser guarantees delivery.
 * sendBeacon only supports POST, so the server has a matching POST endpoint.
 *
 * NOTE (KOA-17630): the chat-history endpoint now requires a verified bridge
 * identity. sendBeacon cannot attach the Authorization/domainName headers the
 * global Axios interceptor adds, so this best-effort unload flush may be
 * rejected (401) by the server. The debounced `saveProjectState` path (which
 * does carry auth) is the durable save; the beacon only covers the final delta
 * on an abrupt close and is acceptable to lose.
 */
export function saveBeacon(
  projectId: string | null,
  tabs: Array<{
    id: string;
    name: string;
    sessionId: string | null;
    isCustomName?: boolean;
    messages: McpChatMessage[];
    chatMode?: string;
    planMode?: boolean;
    planJson?: string | null;
  }>,
  activeTabId: string,
  archivedTabIds?: string[]
): boolean {
  if (!projectId) return false;

  const state = buildProjectState(tabs, activeTabId, archivedTabIds);
  const blob = new Blob([JSON.stringify(state)], { type: "application/json" });
  return navigator.sendBeacon(apiUrl(projectId), blob);
}

export async function loadChatState(projectId: string | null): Promise<PersistedChatState | null> {
  if (!projectId) return null;

  try {
    const res = await fetchWithTimeout(apiUrl(projectId));
    if (res.status === 204 || !res.ok) return null;

    const parsed = await res.json();
    if (!isPersistedChatState(parsed)) return null;
    const state = parsed;
    if (state.version !== 1 || !state.tabs || state.tabs.length === 0) return null;

    // Track latest savedAt for sync
    lastKnownSavedAt = state.savedAt;
    return state;
  } catch {
    return null;
  }
}

export function clearChatState(projectId: string | null): void {
  if (!projectId) return;
  void (async () => {
    try {
      await fetchWithTimeout(apiUrl(projectId), { method: "DELETE" });
    } catch {
      // Fire-and-forget — server-side state will be replaced on the next save.
    }
  })();
}

/**
 * Fire an archive POST so the server flips `archived_at` immediately.
 * Fire-and-forget — callers pair this with adding the id to
 * `pendingArchiveTabIds` on the panel, which flows into the next save's
 * `archivedTabIds` payload so the archive is still atomic with the save
 * even if this POST races the save PUT (or a beforeunload beacon).
 */
export function archiveTab(projectId: string | null, tabId: string): void {
  if (!projectId) return;
  const url = `${apiUrl(projectId)}/tabs/${encodeURIComponent(tabId)}/archive`;
  void (async () => {
    try {
      await fetchWithTimeout(url, { method: "POST" });
    } catch {
      // Fire-and-forget — the next save's `archivedTabIds` field is the
      // race-free fallback (see panel.handleCloseTab pendingArchiveTabIds).
    }
  })();
}

/** Fetch archived tabs (closed/cleared) for a project. Returns
 *  an empty array when the archive is empty or the request fails. */
export async function loadArchivedChatTabs(projectId: string | null): Promise<PersistedChatTab[]> {
  if (!projectId) return [];
  try {
    const res = await fetchWithTimeout(`${apiUrl(projectId)}/archived`);
    if (!res.ok) return [];
    const body = (await res.json()) as { tabs?: PersistedChatTab[] };
    return body.tabs || [];
  } catch {
    return [];
  }
}

/**
 * Check if server has newer data than what we last loaded/saved.
 * Returns the newer state if available, null otherwise.
 */
export async function checkForNewerState(projectId: string | null): Promise<PersistedChatState | null> {
  if (!projectId) return null;

  try {
    const res = await fetchWithTimeout(`${apiUrl(projectId)}/version`);
    if (res.status === 204 || !res.ok) return null;

    const parsed = await res.json();
    if (!isSavedAtEnvelope(parsed)) return null;
    const { savedAt } = parsed;
    if (typeof savedAt !== "number" || savedAt <= lastKnownSavedAt) return null;

    // Server has newer data — fetch full state
    return loadChatState(projectId);
  } catch {
    return null;
  }
}

/**
 * Reset the savedAt sync cursor. Called on project switch so that the first
 * `checkForNewerState` call under the new project doesn't short-circuit
 * against a cursor inherited from the previous project.
 */
export function resetSyncCursor(): void {
  lastKnownSavedAt = 0;
}

// ── Sync tracking ──

let lastKnownSavedAt = 0;
let saveHasFailed = false;

// ── localStorage → server migration ──

interface LegacyStorageRoot {
  [projectId: string]: PersistedChatState;
}

export async function migrateFromLocalStorage(projectId: string): Promise<PersistedChatState | null> {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;

    const root: LegacyStorageRoot = JSON.parse(raw);
    const state = root[projectId];
    if (!state || state.version !== 1 || !state.tabs || state.tabs.length === 0) return null;

    // PUT to server
    const res = await fetchWithTimeout(apiUrl(projectId), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(state)
    });

    if (res.ok) {
      // Remove this project from localStorage
      delete root[projectId];
      if (Object.keys(root).length === 0) {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } else {
        localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(root));
      }
      lastKnownSavedAt = state.savedAt;
    }

    return state;
  } catch {
    return null;
  }
}

// ── Tab restoration (unchanged — pure function) ──

export interface RestoredTab {
  id: string;
  name: string;
  sessionId: string | null;
  isCustomName: boolean;
  messages: McpChatMessage[];
  isLoading: false;
  inputValue: "";
  toolActivities: [];
  expandedCategory: null;
  attachedImages: [];
  attachedFiles: [];
  questionnaires: Record<string, QuestionnaireProgress>;
  visibleMessageCount: 25;
  chatMode: ChatMode;
  effort: EffortTier;
  planMode: boolean;
  selectedElementAttachments: [];
  isTextStreaming: false;
  queuedMessages: [];
  mentions: [];
  mentionCounter: 0;
  isImprovingPrompt: false;
  improveRequestId: null;
  improveOriginalPrompt: null;
  planJson: string | null;
  effectiveCapabilityProfile: null;
  capabilityReason: null;
  capabilityPromotedGroups: null;
  capabilityDenialMessage: null;
}

export function restoreTabs(state: PersistedChatState): { tabs: RestoredTab[]; activeTabId: string } {
  const tabs: RestoredTab[] = state.tabs.map((tab) => ({
    id: tab.id,
    name: tab.name,
    sessionId: tab.sessionId ?? null, // Reuse persisted sessionId so server can restore conversation context
    isCustomName: !!tab.isCustomName,
    messages: tab.messages.map((msg) => {
      // Migrate legacy singular attachment to plural array (additive, no schema bump).
      const elements =
        msg.selectedElementAttachments && msg.selectedElementAttachments.length > 0
          ? msg.selectedElementAttachments
          : msg.selectedElementAttachment
            ? [msg.selectedElementAttachment]
            : undefined;
      const { selectedElementAttachment: _legacy, ...rest } = msg;
      return {
        ...rest,
        selectedElementAttachments: elements,
        // Convert stuck "running" tool statuses to "error"
        toolStatus: msg.toolStatus === "running" ? "error" : msg.toolStatus,
        // Server session is gone — pending interrupts can no longer be acted on
        interruptData: msg.interruptData
          ? {
              ...msg.interruptData,
              status: msg.interruptData.status === "pending" ? ("expired" as const) : msg.interruptData.status
            }
          : undefined
      };
    }),
    isLoading: false as const,
    inputValue: "" as const,
    toolActivities: [] as [],
    expandedCategory: null,
    attachedImages: [] as [],
    attachedFiles: [] as [],
    questionnaires: {} as Record<string, QuestionnaireProgress>,
    visibleMessageCount: 25 as const,
    chatMode: (tab.chatMode === "builder" ? "builder" : tab.chatMode === "ask" ? "ask" : "designer") as ChatMode,
    // Restored tabs adopt the last-used effort (not persisted per-tab — a light
    // preference; getStoredEffort falls back to "lite").
    effort: getStoredEffort(),
    planMode: tab.chatMode === "plan" ? true : tab.planMode || false,
    selectedElementAttachments: [],
    isTextStreaming: false as const,
    queuedMessages: [],
    mentions: [] as [],
    mentionCounter: 0 as const,
    isImprovingPrompt: false as const,
    improveRequestId: null,
    improveOriginalPrompt: null,
    // Drop a plan that belongs to a different tab — it must never resurface on
    // restore and leak into this tab's turns (KOA-17797). Legacy unstamped plans
    // are kept (treated as owned) and get stamped on the next plan event.
    planJson: planJsonForTab(tab.planJson, tab.id),
    effectiveCapabilityProfile: null,
    capabilityReason: null,
    capabilityPromotedGroups: null,
    capabilityDenialMessage: null
  }));

  // Ensure activeTabId references a valid tab
  const activeTabId = tabs.some((t) => t.id === state.activeTabId) ? state.activeTabId : tabs[0].id;

  return { tabs, activeTabId };
}
